import * as Yup from "yup";
import * as configUtils from "util/config";
import * as i18nUtils from "util/i18n";

const getField = (formFieldList, idField) => formFieldList.filter((formField) => formField.idField === idField)[0];

const getFieldValue = (formFieldValues, idField) => {
    if (formFieldValues && formFieldValues[idField]) {
        return formFieldValues[idField];
    }
    return null;
};

const TRUE = 1;
const FALSE = 2;
const HAS_VALUE = 3;
const HAS_VALUE_FIELD = 4;
const HAS_NOT_VALUE = 5;
const HAS_NOT_VALUE_FIELD = 6;
const SHOWN = 7;
const SHOWN_FIELD = 8;
const HIDDEN = 9;
const HIDDEN_FIELD = 10;
const VALUE = 11;
const VALUE_FIELD = 12;
const VALUE_OPERATOR = 13;
const VALUE_RIGHT_SIDE = 14;

const operators = {
    "==": (leftSide, rightSide) => leftSide === rightSide,
    "!=": (leftSide, rightSide) => leftSide !== rightSide,
    ">": (leftSide, rightSide) => leftSide > rightSide,
    ">=": (leftSide, rightSide) => leftSide >= rightSide,
    "<": (leftSide, rightSide) => leftSide < rightSide,
    "<=": (leftSide, rightSide) => leftSide <= rightSide,
};

const isNumeric = (val) => !Number.isNaN(val - parseFloat(val));

function evalSimpleValue(leftSide, operator, rightSide) {
    if (isNumeric(leftSide) && isNumeric(rightSide)) {
        return operators[operator](Number(leftSide), Number(rightSide));
    }
    if (isNumeric(leftSide) || isNumeric(rightSide)) {
        // not matching types
    } else if (typeof leftSide === "string" && typeof rightSide === "string") {
        if (operator === "==") {
            return leftSide === rightSide;
        }
        if (operator === "!=") {
            return leftSide !== rightSide;
        }
        return false;
    }
    return false;
}

// export for test
export const evalOperation = (leftSide, operator, rightSide) => {
    if (Array.isArray(leftSide)) {
        if (operator === "!=") {
            /* if all values evals to true */
            let result = true;
            Object.keys(leftSide).forEach((i) => {
                result = result && evalOperation(leftSide[i], operator, rightSide);
                return result;
            });
        }
        /* if any value evals to true */
        Object.keys(leftSide).forEach((i) => {
            const result = evalOperation(leftSide[i], operator, rightSide);
            return !!result;
        });
        return false;
    }

    let rightTokens = rightSide ? rightSide.toString().split("|") : "";

    if (!(rightTokens.length > 1)) {
        rightTokens = rightSide ? rightSide.toString().split("&") : "";
    }

    if (rightTokens.length > 1) {
        /**
         * we don't support using combined or's and and's
         */
        if (rightSide.indexOf("|") !== -1) {
            return rightTokens.some((token) => evalSimpleValue(leftSide, operator, token));
        }
        return rightTokens.some((token) => !evalSimpleValue(leftSide, operator, token));
    }

    const isValid = evalSimpleValue(leftSide, operator, rightSide);

    if (typeof isValid === "boolean") {
        return isValid;
    }
    /* if not array or numeric or string, we got mismatched types */
    return operator === "!=";
};

const isQuality = (qualityName, idField, fieldList, fieldValues) => {
    let quality = false;
    const field = getField(fieldList, idField);
    // eslint-disable-next-line max-len
    const regex = /^(TRUE)|(FALSE)|(hasValue\((.+)\))|(hasNotValue\((.+)\))|(shown\((.+)\))|(hidden\((.+)\))|(value\((.+)\) (==|!=|<|<=|>=|>) '(.*)')$/;

    const match = regex.exec(field[qualityName]);

    if (match !== null) {
        if (match[TRUE] !== undefined) {
            quality = true;
        } else if (match[FALSE] !== undefined) {
            quality = false;
        } else if (match[HAS_VALUE] !== undefined) {
            quality = !!getFieldValue(fieldValues, match[HAS_VALUE_FIELD]);
        } else if (match[HAS_NOT_VALUE] !== undefined) {
            quality = !getFieldValue(fieldValues, match[HAS_NOT_VALUE_FIELD]);
        } else if (match[SHOWN] !== undefined) {
            quality = isQuality(quality, match[SHOWN_FIELD], fieldList, fieldValues);
        } else if (match[HIDDEN] !== undefined) {
            quality = !isQuality(quality, match[HIDDEN_FIELD], fieldList, fieldValues);
        } else if (match[VALUE] !== undefined) {
            const operator = match[VALUE_OPERATOR];
            const rightSide = match[VALUE_RIGHT_SIDE];

            let leftSide;
            const fieldStr = match[VALUE_FIELD];
            const fieldTokens = fieldStr.split(".");
            if (fieldTokens.length === 1) {
                // Get the value with the field name
                leftSide = getFieldValue(fieldValues, fieldStr);

                if (leftSide !== null && !Array.isArray(leftSide) && typeof leftSide === "object") {
                    leftSide = leftSide.value;
                } else if (leftSide !== null && Array.isArray(leftSide) && leftSide.length > 0) {
                    leftSide = leftSide[0];
                }
            } else if (fieldTokens.length === 2) {
                // Get the value with the field name and then take only a sub part of it,
                // for example debitAccount.currency
                leftSide = getFieldValue(fieldValues, fieldTokens[0]).fieldTokens[1];
            }

            quality = evalOperation(leftSide, operator, rightSide);
        }
    }

    return quality;
};

export const isVisible = (idField, fieldList, fieldValues) => isQuality("visible", idField, fieldList, fieldValues);

export const isRequired = (idField, fieldList, fieldValues) => isQuality("required", idField, fieldList, fieldValues);

export const adjustIdFieldErrors = (errors) => {
    const newErrors = {};
    Object.keys(errors).forEach((key) => {
        newErrors[key.replace(/^_/, "")] = errors[key];
    });
    return newErrors;
};

export const credentialsToUnderscoreFormat = (credentials) =>
    Object.entries(credentials).reduce((values, [key, value]) => ({ ...values, [`_${key}`]: value }), {});

// eslint-disable-next-line max-len, no-useless-escape
export const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]*$/;
export const emailRegexValidation = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
export const validateEmailWithRegex = (_value) => {
    if (_value) {
        // eslint-disable-next-line no-useless-escape
        const reg = new RegExp(configUtils.get("email.validation.regex", "^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,4})+$"), "g");
        if (!reg.test(_value)) {
            return false;
        }
    }
    return true;
};
export const validateCUIT = (value) => {
    if (value) {
        const cuit = value.replace(new RegExp("[^\\d]", "g"), "");
        if (cuit.length !== 11) {
            return false;
        }
        const cuitArray = cuit.split("");
        const serie = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
        let aux = 0;
        for (let i = 0; i < 10; i++) {
            aux += parseInt(cuitArray[i], 10) * serie[i];
        }
        aux = 11 - (aux % 11);
        if (aux === 11) {
            aux = 0;
        }
        if (!Object.is(parseInt(cuitArray[10], 10), aux)) {
            return false;
        }
    }
    return true;
};

export const validateCUIL = (_values, dni) => {
    const values = _values.replace(new RegExp("[^\\d.,]", "g"), "");
    if (values) {
        if (!values.includes(dni)) {
            return false;
        }
        if (values.includes(",") || values.includes(".")) {
            return false;
        }
        if (_values.includes("-")) {
            const reg = new RegExp(
                configUtils.get("onboarding.cuil.validation.pattern", "\\b(20|23|24|27)-[0-9]{8}-[0-9]\\b"),
                "g",
            );
            if (!reg.test(_values) || _values.length !== 13) {
                return false;
            }
        }
        if (!["20", "23", "24", "27"].includes(values.substring(0, 2))) {
            return false;
        }
        if (values.substring(2, 10) !== dni) {
            return false;
        }
        return validateCUIT(values);
    }
    return true;
};

export const validateArcaCUIL = (_values) => {
    const values = _values.replace(new RegExp("[^\\d.,]", "g"), "");
    if (values) {
        if (values.includes(",") || values.includes(".")) {
            return false;
        }
        if (_values.includes("-")) {
            const reg = new RegExp(
                configUtils.get("onboarding.cuil.validation.pattern", "\\b(20|23|24|27)-[0-9]{8}-[0-9]\\b"),
                "g",
            );
            if (!reg.test(_values) || _values.length !== 13) {
                return false;
            }
        }
        if (!["20", "23", "24", "27"].includes(values.substring(0, 2))) {
            return false;
        }

        if (values.length !== 11) {
            return false;
        }
    }
    return true;
};

export const validateCUILPF = (_value) => {
    if (_value) {
        const reg = new RegExp(configUtils.get("onboarding.cuil.validation.pf", "\b(20|23|24|27)[0-9]{8}[0-9]\b"), "g");
        if (!reg.test(_value) || _value.length !== 11) {
            return false;
        }
        return validateCUIT(_value);
    }
    return true;
};

export const validatePhone = (argAreaCodes) =>
    Yup.object().shape({
        countryCode: Yup.string(),
        areaCode: Yup.string().when("countryCode", {
            is: configUtils.get("phone.countryCode.arg", "54"),
            then: Yup.string().test("format", i18nUtils.get("phone.arg.areaCode.invalid"), (value, context) => {
                if (value.length + context.parent.value.length <= configUtils.getInteger("phone.arg.length", 10)) {
                    return argAreaCodes.findIndex((code) => code === value) !== -1;
                }
                return true;
            }),
        }),
        value: Yup.string()
            .when("countryCode", {
                is: configUtils.get("phone.countryCode.arg", "54"),
                then: Yup.string().test(
                    "format",
                    i18nUtils.get("phone.arg.length.error"),
                    (value, context) =>
                        value.length + context.parent.areaCode.length <= configUtils.getInteger("phone.arg.length", 10),
                ),
            })
            .required(i18nUtils.get("onboarding.step.verifySMS.mobilePhone.error.empty")),
    });

export const validatePhonePayway = (argAreaCodes) =>
    Yup.object().shape({
        countryCode: Yup.string(),
        areaCode: Yup.string().when("countryCode", {
            is: configUtils.get("phone.countryCode.arg", "54"),
            then: Yup.string().test("format", i18nUtils.get("phone.arg.areaCode.invalid"), (value, context) => {
                const totalLength = value.length + context.parent.value.length;
                if (
                    totalLength === configUtils.getInteger("phone.arg.length", 10) ||
                    totalLength === configUtils.getInteger("payway.cellphone.length", 12)
                ) {
                    return argAreaCodes.findIndex((code) => code === value) !== -1;
                }
                return true;
            }),
        }),
        value: Yup.string()
            .when("countryCode", {
                is: configUtils.get("phone.countryCode.arg", "54"),
                then: Yup.string()
                    .test(
                        "format",
                        i18nUtils.get(
                            "payway.terminals.cellphone.format.validation.error",
                            "Debe agregar el 15 como prefijo para el teléfono móvil",
                        ),
                        (value, context) => {
                            const totalLength = value.length + context.parent.areaCode.length;

                            if (totalLength > configUtils.getInteger("phone.arg.length", 10)) {
                                return (
                                    totalLength === configUtils.getInteger("payway.cellphone.length", 12) &&
                                    value.slice(0, 2) === "15"
                                );
                            }
                            return true;
                        },
                    )
                    .test(
                        "format2",
                        i18nUtils.get(
                            "payway.terminals.phone.length.validation.error",
                            "El número de teléfono debe tener 10 dígitos para fijos y 12 para celulares",
                        ),
                        (value, context) => {
                            const totalLength = value.length + context.parent.areaCode.length;
                            return (
                                totalLength === configUtils.getInteger("phone.arg.length", 10) ||
                                totalLength === configUtils.getInteger("payway.cellphone.length", 12)
                            );
                        },
                    ),
            })
            .required(i18nUtils.get("onboarding.step.verifySMS.mobilePhone.error.empty")),
    });

export const validateCUITWithRegex = (_value) => {
    if (_value) {
        // eslint-disable-next-line no-useless-escape
        const reg = new RegExp(configUtils.get("cuit.validation.regex", "\b(20|2[3-7]|30|3[3-4])(d{8})(d)\b"), "g");
        if (!reg.test(_value)) {
            return false;
        }
        return validateCUIT(_value);
    }
    return true;
};

export const openWindowWithPost = (url, data) => {
    const form = document.createElement("form");
    form.target = "_blank";
    form.method = "POST";
    form.action = url;

    const keys = Object.keys(data);
    const values = Object.values(data);
    for (let i = 0; i < keys.length; i += 1) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = keys[i];
        input.value = values[i];
        form.appendChild(input);
    }

    document.body.appendChild(form);
    return form;
};
